import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { MagnifyingGlass, Plus, ArrowLeft } from "@phosphor-icons/react";
import { toast } from "sonner";
import { ModalShell } from "../components/ModalShell";
import api, { fmtGNF, fmtDate, apiError, logError } from "../lib/api";
import { StatusBadge } from "../components/StatusBadge";
import { AmountInput } from "../components/AmountInput";

export function Clients() {
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", company_name: "", business_number: "", address: "", note: "" });

  const load = useCallback(() => api.get(`/clients${search ? `?search=${encodeURIComponent(search)}` : ""}`).then((r) => setClients(r.data)).catch((e) => logError(e)), [search]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const create = async () => {
    try {
      await api.post("/clients", form);
      toast.success("Client créé"); setShowAdd(false);
      setForm({ name: "", phone: "", company_name: "", business_number: "", address: "", note: "" }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4 fade-in" data-testid="clients-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-bold text-2xl">Clients</h1>
        <button data-testid="add-client-btn" onClick={() => setShowAdd(true)} className="btn-ghost py-2 px-4 flex items-center gap-1.5 text-sm"><Plus size={16} /> Nouveau client</button>
      </div>
      <div className="relative">
        <MagnifyingGlass size={18} className="absolute left-3.5 top-3 text-gray-400" />
        <input data-testid="clients-search-input" className="kinput pl-10" placeholder="Rechercher par nom ou téléphone" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      <div className="space-y-2">
        {clients.map((c) => (
          <Link key={c.id} to={`/clients/${c.id}`} data-testid="client-row" className="kcard p-4 flex items-center gap-3 hover:shadow-md transition-shadow">
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-sm">{c.name} <span className="font-normal text-gray-500">· {c.phone}</span></p>
              <p className="text-xs text-gray-500">CA total : {fmtGNF(c.total_ca)}</p>
            </div>
            {c.outstanding > 0 && <span className="badge-partial px-2.5 py-1 rounded-full text-xs font-semibold tabular-nums">Doit {fmtGNF(c.outstanding)}</span>}
          </Link>
        ))}
        {clients.length === 0 && <div className="kcard p-8 text-center text-sm text-gray-400">Aucun client identifié pour l'instant.</div>}
      </div>

      {showAdd && (
        <ModalShell size="max-w-sm" onClose={() => setShowAdd(false)}>
            <h3 className="font-display font-bold text-lg">Nouveau client</h3>
            <input data-testid="client-name-input" className="kinput" placeholder="Nom (obligatoire)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            <input data-testid="client-phone-input" className="kinput" placeholder="Téléphone (obligatoire)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            <p className="text-xs text-gray-500 pt-1">Facultatif — utile pour une facturation professionnelle.</p>
            <input data-testid="client-company-input" className="kinput" placeholder="Nom de l'entreprise cliente (optionnel)" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} />
            <input data-testid="client-business-number-input" className="kinput" placeholder="N° d'entreprise du client (optionnel)" value={form.business_number} onChange={(e) => setForm({ ...form, business_number: e.target.value })} />
            <input data-testid="client-address-input" className="kinput" placeholder="Adresse / quartier (optionnel)" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            <textarea data-testid="client-note-input" rows={2} className="kinput" placeholder="Note libre (optionnel)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            <div className="flex gap-2">
              <button className="btn-ghost flex-1 py-2.5" onClick={() => setShowAdd(false)}>Annuler</button>
              <button data-testid="client-create-btn" className="btn-amber flex-1 py-2.5" disabled={!form.name || !form.phone} onClick={create}>Créer</button>
            </div>
          </ModalShell>
      )}
    </div>
  );
}

export function ClientDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [payId, setPayId] = useState(null);
  const [payAmount, setPayAmount] = useState("");

  const load = useCallback(() => api.get(`/clients/${id}`).then((r) => setC(r.data)).catch((e) => logError(e)), [id]);
  useEffect(() => { load(); }, [load]);

  const doPay = async () => {
    try {
      await api.post(`/sales/${payId}/pay`, { amount: parseFloat(payAmount) });
      toast.success("Règlement encaissé"); setPayId(null); setPayAmount(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!c) return <p className="text-sm text-gray-400 py-10">Chargement...</p>;

  return (
    <div className="max-w-2xl space-y-4 fade-in" data-testid="client-detail-page">
      <button onClick={() => navigate("/clients")} className="flex items-center gap-1 text-sm text-gray-500"><ArrowLeft size={16} /> Clients</button>
      <div className="kcard p-5">
        <h1 className="font-display font-bold text-2xl">{c.name}</h1>
        <p className="text-sm text-gray-500">{c.phone}</p>
        {(c.company_name || c.business_number || c.address) && (
          <div data-testid="client-business-info" className="text-xs text-gray-500 mb-3 space-y-0.5">
            {c.company_name && <p>Entreprise : {c.company_name}</p>}
            {c.business_number && <p>N° entreprise : {c.business_number}</p>}
            {c.address && <p>{c.address}</p>}
          </div>
        )}
        {c.note && <p data-testid="client-note" className="text-xs text-gray-600 bg-[#F6F4EF] rounded-xl px-3 py-2 mb-3">{c.note}</p>}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-[#F6F4EF] rounded-2xl p-3 text-center"><p className="text-xs text-gray-500">CA total généré</p><p className="font-display font-bold tabular-nums">{fmtGNF(c.total_ca)}</p></div>
          <div className="bg-[#F6F4EF] rounded-2xl p-3 text-center"><p className="text-xs text-gray-500">Créances en cours</p><p className={`font-display font-bold tabular-nums ${c.outstanding > 0 ? "text-[#B4432F]" : "text-[#2E7D5B]"}`}>{fmtGNF(c.outstanding)}</p></div>
        </div>
      </div>
      <div className="kcard p-5">
        <h3 className="font-display font-bold mb-3">Historique des achats</h3>
        <div className="space-y-2">
          {c.sales.map((s) => (
            <div key={s.id} data-testid="client-sale-row" className={`px-3 py-2.5 rounded-xl bg-[#F6F4EF]/60 ${s.status === "cancelled" ? "opacity-50" : ""}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono-doc text-xs font-semibold">{s.number}</span>
                <span className="text-xs text-gray-400">{fmtDate(s.created_at)}</span>
                <span className="flex-1" />
                <span className="font-semibold text-sm tabular-nums">{fmtGNF(s.total)}</span>
                {s.status === "cancelled" ? <span className="badge-unpaid px-2 py-0.5 rounded-full text-xs">Annulée</span>
                  : <StatusBadge status={s.payment_status} remaining={s.total - s.amount_paid} fmt={fmtGNF} />}
                {s.status === "active" && s.payment === "credit" && s.payment_status !== "paid" && (
                  <button data-testid="client-pay-btn" onClick={() => { setPayId(s.id); setPayAmount(""); }} className="text-xs text-[#2E7D5B] font-semibold">Encaisser</button>
                )}
              </div>
            </div>
          ))}
          {c.sales.length === 0 && <p className="text-sm text-gray-400">Aucun achat.</p>}
        </div>
      </div>
      {payId && (
        <ModalShell size="max-w-sm" onClose={() => setPayId(null)}>
            <h3 className="font-display font-bold text-lg mb-3">Encaisser un règlement</h3>
            <AmountInput data-testid="client-pay-amount-input" onOverflow={(m) => toast.error(m)} className="mb-3" placeholder="Montant reçu (GNF)" value={payAmount} onChange={setPayAmount} />
            <div className="flex gap-2">
              <button className="btn-ghost flex-1 py-2.5" onClick={() => setPayId(null)}>Retour</button>
              <button data-testid="client-pay-confirm-btn" className="btn-amber flex-1 py-2.5" disabled={!payAmount} onClick={doPay}>Encaisser</button>
            </div>
          </ModalShell>
      )}
    </div>
  );
}
